/**
 * Client-side API types. Shapes already in the shared contract
 * (@app/core/api-dto, browser-safe subpath) are re-exported from it; the rest
 * are still mirrored here until they migrate (plan item 2.10) — for those, a
 * change to the API contract must be made on both sides.
 */

/*
 * Product / passport wire types come from the shared API contract in
 * @app/core/api-dto (zod schemas the API is contract-tested against), not from
 * a hand-kept mirror here. The status/scheme unions below derive from them.
 */
import type { DisposalLineRequest } from "@app/core/disposal";
import type {
  CreateAccessGrantRequest,
  CreateOrgInviteRequest,
  CreateOrgRequest,
  CreateOrgSelfRequest,
  CreateApiKeyRequest,
  CreateSupplierInviteRequest,
  CreateWebhookRequest,
  OperatorBodyRequest,
  UpdateWebhookRequest,
} from "@app/core/schemas";
import type {
  AccessGrant,
  ApiKey,
  AuthTokensWire,
  DisposalLine,
  LicenseStatusLite,
  LoginResultWire,
  PublicUserWire,
  OrgStats,
  EconomicOperator,
  Invoice,
  Org,
  OrgInvite,
  OrgMember,
  OrgWithMemberRoleWire,
  Identifier,
  Passport,
  Product,
  ProductDocument,
  RegistryRegistration,
  SupplierInvite,
  Webhook,
  WebhookDelivery,
} from "@app/core/api-dto";
export type {
  AcceptInviteResult,
  LicenseStatusFull,
  LicenseStatusLite,
  MfaRequired,
  PasswordExpired,
  SecurityOverview,
  SelfActivityEvent,
  SessionInfo,
  AdminPassportRow,
  AdminProductRow,
  AdminUserRow,
  OrgStats,
  OrgWithCounts,
  PlatformOverview,
  DisposalLine,
  DisposalOverview,
  DisposalReport,
  BillingUsage,
  CloseMonthResult,
  Invoice,
  OrgBillingView,
  AccessGrant,
  ApiKey,
  ChainVerification,
  CreateOrgInviteResult,
  InvitePreview,
  Org,
  OrgDetail,
  OrgInvite,
  OrgMember,
  CreateAccessGrantResult,
  CreateApiKeyResult,
  CreateWebhookResult,
  EconomicOperator,
  EffectiveOperator,
  ProductDocument,
  Identifier,
  PassportBackup,
  PassportDetail,
  RegistryRegistration,
  Passport,
  PassportVersion,
  PassportVersionSummary,
  Product,
  ProductDetail,
  ProductWithCompliance,
  ReadinessIssue,
  CreateSupplierInviteResult,
  SupplierInvite,
  SupplierSubmissionFieldDiff,
  SupplierSubmissionReview,
  Webhook,
  WebhookDelivery,
} from "@app/core/api-dto";
export type Permission = string;

export interface ApiErrorShape {
  error: {
    code: string;
    /** Granular, stable reason (see @app/core error-catalog) — the client's message key. */
    reason?: string;
    message: string;
    /** Echoed back for support; matches the `x-correlation-id` header. */
    correlationId?: string;
    details?: unknown;
  };
}

export type LicenseState = LicenseStatusLite["state"];
export type PublicUser = PublicUserWire;
export type AuthTokens = AuthTokensWire;
export type LoginResult = LoginResultWire;

/* --- DPP domain (organizations, products, passports) ------------------------ */

/** Org-level (tenant-scoped) role — mirrors @app/core ORG_ROLES. */
export type OrgMemberRole = OrgMember["role"];

/**
 * Commercial SaaS package — mirrors @app/core BILLING_PLANS. Two sellable
 * packages (free, unlimited); "none" = never billed (on-prem/internal), and the
 * enterprise offer is an on-prem license.
 */
export type BillingPlan = Org["billingPlan"];

export type OrgStatus = Org["status"];
export type EnterpriseSize = NonNullable<Org["enterpriseSize"]>;

/* --- Unsold-goods destruction disclosure (draft TR ESPR Art. 23–26) ---------- */

export type DisposalCategory = DisposalLine["productCategory"];
export type DisposalReason = DisposalLine["reason"];
export { DISPOSAL_CATEGORIES, DISPOSAL_REASONS, ENTERPRISE_SIZES } from "@app/core/disposal";
/** One disclosure line as the panel sends it — the API's own zod input type. */
export type DisposalLineBody = DisposalLineRequest;

/** GET /orgs — the caller's organizations, joined with their org-level role. */
export interface OrgWithMemberRole extends OrgWithMemberRoleWire {
  /** Client-side only: a platform admin sees this org without being a member. */
  viaPlatform?: boolean;
}

/** List envelope of the platform-console endpoints ({ items, total }). */
export interface Page<T> {
  items: T[];
  total: number;
}

/** GET /admin/roles — a global (platform) role. */
export interface PlatformRole {
  id: string;
  name: string;
  description: string | null;
}

/* --- Org invitations (email onboarding) ------------------------------------- */

export type OrgInviteStatus = OrgInvite["status"];

/* --- Billing (SaaS packages, per calendar year) ------------------------------ */

export type InvoiceStatus = Invoice["status"];

/** "subscription" = the yearly base fee ("YYYY"); "usage" = legacy monthly overage ("YYYY-MM"). */
export type InvoiceKind = Invoice["kind"];

/** One issuance bucket of the dashboard bar chart. */
export type OrgStatsIssuance = OrgStats["issuance"][number];
export type ProductStatus = Product["status"];

/* --- Product documents (draft TR ESPR Art. 27(7)–(9)) ------------------------- */

export type DocumentKind = ProductDocument["kind"];
export { DOCUMENT_KINDS } from "@app/core/schemas";

export type IdentifierScheme = Identifier["scheme"];
export type IdentifierGranularity = Identifier["granularity"];
/** One-way lifecycle: a withdrawn identifier keeps resolving but cannot anchor a new passport. */
export type IdentifierStatus = Identifier["status"];

/** Standard list envelope of the DPP list endpoints. */
export interface Paged<T> {
  items: T[];
  total: number;
  limit: number;
  offset: number;
}

export interface ImportRowError {
  row: number;
  message: string;
}

export interface ImportResult {
  created: number;
  updated: number;
  errors: ImportRowError[];
}

export type PassportStatus = Passport["status"];

export type LineageReason = NonNullable<Passport["lineageReason"]>;
export const LINEAGE_REASONS: LineageReason[] = ["refurbished", "remanufactured", "repurposed", "reissued"];

export type RegistryRegistrationStatus = RegistryRegistration["status"];

/** Shape of `ApiError.details` on a publish 4xx (missing/invalid fields). */
export interface PublishValidationDetails {
  fields?: string[];
  issues?: { field: string; message: string }[];
}

/* --- Supplier data collection ------------------------------------------------ */

export type SupplierInviteStatus = SupplierInvite["status"];
export type CreateSupplierInviteBody = CreateSupplierInviteRequest;

/* --- Restricted-tier access grants (EN 18239) --------------------------------- */

/* --- Economic operators (draft TR ESPR Art. 27–29) --------------------------- */

export type OperatorRole = EconomicOperator["role"];
export type OperatorIdentifierScheme = NonNullable<EconomicOperator["identifierScheme"]>;
export { OPERATOR_IDENTIFIER_SCHEMES, OPERATOR_ROLES } from "@app/core/schemas";
/** Create/update body — the API's own zod input schema, so client and server validate alike. */
export type OperatorBody = OperatorBodyRequest;

export type AccessGrantRole = AccessGrant["role"];
/** "expired" is derived from expiresAt server-side — never persisted. */
export type AccessGrantStatus = AccessGrant["status"];
export type ApiKeyScope = ApiKey["scopes"][number];
/** "expired" is derived from expiresAt server-side — never persisted. */
export type ApiKeyStatus = ApiKey["status"];
export type WebhookEvent = Webhook["events"][number];
export type WebhookStatus = Webhook["status"];
export type WebhookDeliveryStatus = WebhookDelivery["status"];
export { API_KEY_SCOPES, WEBHOOK_EVENTS } from "@app/core/schemas";

/* Request bodies are the API's own zod input types, so client and server agree. */
export type CreateOrgBody = CreateOrgRequest;
export type CreateOrgSelfBody = CreateOrgSelfRequest;
export type CreateOrgInviteBody = CreateOrgInviteRequest;
export type CreateAccessGrantBody = CreateAccessGrantRequest;
export type CreateApiKeyBody = CreateApiKeyRequest;
export type CreateWebhookBody = CreateWebhookRequest;
export type UpdateWebhookBody = UpdateWebhookRequest;
